import { Session } from 'next-auth';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
    DirectionEnum,
    FlexBox,
    GapSize,
    IconName,
    Justify,
    Tab,
    TabList,
    Tabs,
    TextLevels,
} from '@bytel/trilogy-react';

import { getServerSession } from '@actions/get-session';

import { ExtraCmsBlockByTab, FaiTabsEnum, TabConfig, TabConfigItem } from '@app-types/home-fai';
import { CategoryCodeInfo, FaiType, OffersByCategoryCode } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { BigLame, BigLameProps } from '@components/big-lame';
import { DisneyIcon } from '@components/disney';
import { InlinePrice } from '@components/inline-price';

import { PlanTypeId } from '@constants/plan';

import { filterOut4GWhen5GExists } from '@eligibility/helpers';
import { ConnectionIncentive } from '@home-fai/components';
import { CATEGORY_MAPPING } from '@home-fai/helpers';

import { RocSelector } from './roc-card/roc-selector';
import { SSIBlock } from './ssi-block';

interface FaiOffersByTabsProps {
    tabConfig: TabConfig;
    defaultTab: FaiTabsEnum;
    setActiveTab?: (tab: FaiTabsEnum) => void;
    offersByCategory: OffersByCategoryCode;
    categoriesToShow: PlanTypeId[];
    plans: { [key: string]: FaiType };
    onAddToCart: (gencode: string) => void;
    showComparatorSelection?: boolean;
    showDetailsButton?: boolean;
    extraCmsBlockByTab?: ExtraCmsBlockByTab;
}

function tabSorter(a: [string, TabConfigItem], b: [string, TabConfigItem]) {
    return a[1].order - b[1].order;
}

function getOffersByCategory(plans: { [key: string]: FaiType }, activeTabCategoryOffers?: CategoryCodeInfo[]) {
    return activeTabCategoryOffers?.filter((o) => {
        const plan = plans[`${o.rangeNg}_${o.commercialSeries}-${o.technology}`];
        return plan !== undefined;
    });
}

const bigLameConfig: Partial<Record<PlanTypeId, BigLameProps['elements']>> = {
    [PlanTypeId.FAI_ONGLET_BBOX]: [
        {
            key: 'big',
            iconName: IconName.BOX_WIFI_6,
            text: (
                <>
                    <InlinePrice amount={-7} period='mois' level={TextLevels.THREE} /> sur votre Bbox
                    <br />
                    même après 12&nbsp;mois
                </>
            ),
        },
        {
            key: 'mobile',
            iconName: IconName.SIM_CARD,
            text: (
                <>
                    <InlinePrice amount={-5} period='mois' level={TextLevels.THREE} /> minimum sur votre forfait
                    <br />
                    mobile 20&nbsp;Go ou&nbsp;+
                </>
            ),
        },
        {
            key: 'disney',
            image: <DisneyIcon height={20} />,
            text: (
                <>
                    <InlinePrice amount={-5} period='mois' level={TextLevels.THREE} /> sur l&apos;option Disney+
                    <br />
                    standard
                </>
            ),
        },
    ],
    [PlanTypeId.FAI_ONGLET_FIN_DE_SERIE]: [
        {
            key: 'mobile',
            iconName: IconName.SIM_CARD,
            text: (
                <>
                    <InlinePrice amount={-5} period='mois' level={TextLevels.THREE} /> minimum sur votre forfait
                    <br />
                    mobile 20&nbsp;Go ou&nbsp;+
                </>
            ),
        },
        {
            key: 'disney',
            image: <DisneyIcon height={20} />,
            text: (
                <>
                    <InlinePrice amount={-5} period='mois' level={TextLevels.THREE} /> sur l&apos;option Disney+
                    <br />
                    standard
                </>
            ),
        },
        {
            key: 'giga-boost',
            iconName: IconName.SHOOTING_STAR,
            text: (
                <>
                    <b>Giga Boost&nbsp;:</b> ajoutez 20 Go sur
                    <br />
                    chaque ligne BiG de vos proches.
                </>
            ),
        },
    ],
};

export function FaiOffersByTabs({
    tabConfig,
    defaultTab,
    setActiveTab = () => {},
    offersByCategory,
    categoriesToShow,
    plans,
    onAddToCart,
    showComparatorSelection,
    showDetailsButton,
    extraCmsBlockByTab,
}: FaiOffersByTabsProps) {
    const [currentTab, setCurrentTab] = useState<FaiTabsEnum>(defaultTab);
    const activeTabCategory = CATEGORY_MAPPING[currentTab];
    const [session, setSession] = useState<Session | null>(null);
    const user = session?.user as UserType | undefined;

    useLayoutEffect(() => {
        if (defaultTab !== currentTab) {
            setCurrentTab(defaultTab);
        }
    }, [defaultTab]);

    const setTab = (tab: FaiTabsEnum) => {
        if (tab === currentTab) return;
        setCurrentTab(tab);
        window.location.hash = tab;
        setActiveTab(tab);
    };

    const tabsToRender = Object.entries(tabConfig)
        .sort(tabSorter)
        .filter(([key]) => {
            const mappedCategory = CATEGORY_MAPPING[key as FaiTabsEnum];
            if (!mappedCategory) {
                return false;
            }
            const categoryOffers = offersByCategory[mappedCategory];
            return (
                categoryOffers !== undefined &&
                categoryOffers.length > 0 &&
                categoryOffers.some((o) => {
                    const plan = plans[`${o.rangeNg}_${o.commercialSeries}-${o.technology}`];
                    return plan && o.categories?.some((c) => categoriesToShow?.includes(c));
                })
            );
        });

    const { hasSiblingHat, activeTabCategoryOffers } = useMemo(() => {
        let hasSiblingHat = false;
        const activeTabCategoryOffers = offersByCategory[activeTabCategory]?.filter((o) => {
            const plan = plans[`${o.rangeNg}_${o.commercialSeries}-${o.technology}`];

            hasSiblingHat = hasSiblingHat || !!plan?.hat;

            return plan && o.categories?.some((c) => categoriesToShow.includes(c));
        });

        return {
            hasSiblingHat,
            activeTabCategoryOffers: activeTabCategoryOffers
                ? filterOut4GWhen5GExists(activeTabCategoryOffers)
                : activeTabCategoryOffers,
        };
    }, [activeTabCategory]);
    const hasBigOffers = activeTabCategoryOffers?.some((offer) => offer.categories.includes(PlanTypeId.FAI_BIG));

    useEffect(() => {
        const availableTabs = tabsToRender.map(([key]) => key as FaiTabsEnum);
        if (!availableTabs.includes(currentTab)) {
            const firstAvailableTab = availableTabs[0];
            setTab(firstAvailableTab ?? defaultTab);
        }
    }, [tabsToRender, currentTab]);

    useEffect(() => {
        getServerSession()
            .then((_session) => {
                setSession(_session);
            })
            .catch(() => setSession(null));
    }, []);

    const currentTabCmsBlock = extraCmsBlockByTab?.[currentTab];

    return (
        <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FIVE}>
            {tabsToRender.length > 1 && (
                <Tabs small>
                    <TabList className='is-justified-center'>
                        {tabsToRender.map(([key, tab]) => {
                            const tabKey = key as FaiTabsEnum;
                            return (
                                <Tab
                                    routerLink='a'
                                    key={tabKey}
                                    label={tab.name}
                                    onClick={() => setTab(tabKey)}
                                    href={`#${tabKey}`}
                                    id={tabKey}
                                    active={currentTab === tabKey}
                                    iconName={tab.icon}
                                />
                            );
                        })}
                    </TabList>
                </Tabs>
            )}
            {hasBigOffers && !user && bigLameConfig[activeTabCategory] && (
                <BigLame
                    title={
                        <>
                            Les avantages
                            <br />
                            avec un forfait mobile
                        </>
                    }
                    elements={bigLameConfig[activeTabCategory]}
                />
            )}
            {user && <ConnectionIncentive />}
            <FlexBox
                gap={{ mobile: GapSize.FIVE, tablet: GapSize.FIVE, desktop: GapSize.FIVE }}
                wrap
                justify={Justify.CENTER}
            >
                {getOffersByCategory(plans, activeTabCategoryOffers)?.map((o) => (
                    <RocSelector
                        key={`${o.rangeNg}-${o.commercialSeries}`}
                        plan={plans[`${o.rangeNg}_${o.commercialSeries}-${o.technology}`]}
                        onAddToCart={onAddToCart}
                        hasSiblingHat={hasSiblingHat}
                        activeTab={currentTab}
                        showComparatorCheckbox={showComparatorSelection}
                        showDetailsButton={showDetailsButton}
                    />
                ))}
            </FlexBox>
            {tabsToRender.length > 0 && currentTabCmsBlock && <SSIBlock url={currentTabCmsBlock} />}
        </FlexBox>
    );
}
